/**
 * seaMonsters — de fire havuhyrer der ligger ude i havet på vide udsnit.
 *
 * Hver post peger på en frilagt fil fra scripts/build-sea-monsters.mjs og på
 * det stykke hav dens egen billedtekst udpeger: Oceani Germanici er Nordsøen,
 * Morsus Norvegicus hører til nordnorske farvande. `seaMonsterCorners` regner
 * rammen ud i Mercator, så træsnittet ikke trykkes fladt jo længere nord det
 * ligger — MapLibre strækker et image-source lineært mellem hjørnerne.
 *
 * Læses af hooks/useSeaMonsters.ts og af App.tsx' render.
 */

export interface SeaMonster {
  id: string;
  /** Sti under public/. */
  file: string;
  /** Pixelbredde delt med pixelhøjde i den byggede fil. */
  aspect: number;
  /** Midtpunkt, [lng, lat]. */
  centre: [number, number];
  /** Bredde i længdegrader; højden følger af aspect. */
  widthDeg: number;
  /** Værkets navn og hvor træsnittet står. */
  credit: string;
  /** Hvor filen er hentet, eller tom hvis det ikke er nedskrevet. */
  sourceUrl: string;
}

export const SEA_MONSTERS: SeaMonster[] = [
  {
    id: "soeorm",
    file: "/sea-monsters/soeorm.webp",
    aspect: 2.0645,
    centre: [0, 62.75],
    widthDeg: 6,
    credit: "Søormen — Olaus Magnus, Historia de gentibus septentrionalibus (Rom 1555), bog 21",
    sourceUrl: "",
  },
  {
    id: "vacca-marina",
    file: "/sea-monsters/vacca-marina.webp",
    aspect: 1.4713,
    centre: [-7.5, 66.5],
    widthDeg: 6,
    credit: "Vacca marina — Gesner, Addenda s. 369 (1560), stukket af R.C. West i PZSL 1853, s. 113",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:PZSL1853Page113.png",
  },
  {
    id: "porcus-monstrosus",
    file: "/sea-monsters/porcus-monstrosus.webp",
    aspect: 2.0779,
    centre: [3.6, 56.3],
    widthDeg: 4.4,
    credit: "Porcus monstrosus Oceani Germanici — Olaus Magnus 1568, s. 788, stukket af R.C. West i PZSL 1853, s. 113",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:PZSL1853Page113.png",
  },
  {
    id: "rosmarus",
    file: "/sea-monsters/rosmarus.webp",
    aspect: 1.8182,
    centre: [22, 72.5],
    widthDeg: 8,
    credit: "Rosmarus — Gesner, Addenda s. 368 (1560), stukket af R.C. West i PZSL 1853, s. 112",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:PZSL1853Page112.png",
  },
];

/**
 * Over dette zoomniveau hentes uhyrerne ikke, og de der er hentet lægges væk.
 * Laget er tonet til 0 ved zoom 8 (se paint i App.tsx), så en halv grad over
 * er der intet at se, og skiftet kan ikke opleves som et pop.
 */
export const SEA_MONSTER_MAX_ZOOM = 8.5;

const RAD = Math.PI / 180;

/** Web Mercator-y i grader, så højden kan regnes i samme enhed som bredden. */
export function mercY(lat: number): number {
  return Math.log(Math.tan(Math.PI / 4 + (lat * RAD) / 2)) / RAD;
}

export function mercLat(y: number): number {
  return (2 * Math.atan(Math.exp(y * RAD)) - Math.PI / 2) / RAD;
}

/**
 * Hjørnerne i den rækkefølge MapLibres image-source vil have dem:
 * øverst til venstre, øverst til højre, nederst til højre, nederst til venstre.
 */
export function seaMonsterCorners(m: SeaMonster): [[number, number], [number, number], [number, number], [number, number]] {
  const [lng, lat] = m.centre;
  const halvBredde = m.widthDeg / 2;
  const halvHoejde = m.widthDeg / m.aspect / 2;
  const y = mercY(lat);
  const nord = mercLat(y + halvHoejde);
  const syd = mercLat(y - halvHoejde);
  return [
    [lng - halvBredde, nord],
    [lng + halvBredde, nord],
    [lng + halvBredde, syd],
    [lng - halvBredde, syd],
  ];
}
